import { getToken } from '@clerk/vue'
import { captureApiError } from './sentry'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
    public readonly response?: Response,
  ) {
    super(detail)
    this.name = 'ApiError'
  }
}

// getToken() can return null briefly after first login while Clerk fetches
// the initial JWT. Retry once after a short delay before giving up.
async function getAuthToken(): Promise<string | null> {
  const token = await getToken()
  if (token) return token
  await new Promise((r) => setTimeout(r, 300))
  return getToken()
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getAuthToken()

  const headers = new Headers(options.headers)
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  if (options.body) {
    headers.set('Content-Type', 'application/json')
  }

  let response!: Response
  let networkError: unknown
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      response = await fetch(`${BASE_URL}${path}`, { ...options, headers })
      networkError = undefined
      break
    } catch (err) {
      networkError = err
      if (attempt === 0) await new Promise((r) => setTimeout(r, 1000))
    }
  }
  if (networkError) {
    captureApiError(networkError, { endpoint: path })
    throw networkError
  }

  if (response.status === 401) {
    // Only hard-redirect if Clerk has no active session; a 401 while Clerk's
    // token is still initializing should not boot the user to sign-in.
    if (!window.Clerk?.user) {
      window.location.href = '/sign-in'
    }
    throw new ApiError(401, 'Unauthorized', response)
    // 401 is NOT captured — expected auth flow
  }

  if (!response.ok) {
    let detail: string
    try {
      const body = await response.json()
      detail = body.detail ?? response.statusText
    } catch {
      detail = response.statusText
    }
    const error = new ApiError(response.status, detail, response)
    // Capture 5xx to Sentry; skip 4xx (expected app flow / validation)
    if (response.status >= 500) {
      captureApiError(error, { endpoint: path, status: response.status })
    }
    throw error
  }

  if (response.status === 204) {
    return undefined as T
  }
  return response.json() as Promise<T>
}

export const apiClient = {
  get: <T>(path: string, options?: RequestInit) => request<T>(path, options),
  post: <T>(path: string, body: unknown, options?: RequestInit) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body), ...options }),
  put: <T>(path: string, body: unknown, options?: RequestInit) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body), ...options }),
  patch: <T>(path: string, body: unknown, options?: RequestInit) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body), ...options }),
  delete: <T>(path: string, options?: RequestInit) => request<T>(path, { method: 'DELETE', ...options }),
}
